<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <component
    :is="tag"
    class="ue-inline-editable rounded"
    ref="editableElem"
    v-click-outside="saveField"
    v-html="convertBreakLinesToBr(props.value || '')"
    @click="enterEdit"
    @keyup.esc.prevent="cancelEdit"
    :contenteditable="isEditing"
    :data-placeholder="props.placeholder"
    :class="{
      'show-placeholder border-on-hover': editable,
      'text-truncate': truncate,
    }"
  />
</template>

<script setup lang="ts">
import { ref, defineProps, defineEmits, nextTick, computed } from 'vue'
import vClickOutside from '@/lib/directives/clickOutside'
import DOMPurify from 'dompurify'

const htmlToPlainText = (html: string): string => {
  const tmp = document.createElement('div')
  tmp.innerHTML = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['br', 'div', 'p'],
  })

  tmp.querySelectorAll('br').forEach((br) => {
    br.replaceWith('\n')
  })

  tmp.querySelectorAll('div').forEach((div) => {
    if (div.textContent?.trim()) {
      div.replaceWith('\n' + (div.textContent || ''))
    } else {
      div.replaceWith('\n')
    }
  })

  tmp.querySelectorAll('p').forEach((p) => {
    if (p.textContent?.trim()) {
      p.replaceWith('\n' + (p.textContent || ''))
    } else {
      p.replaceWith('\n')
    }
  })

  return tmp.textContent?.trim() || ''
}

const convertBreakLinesToBr = (html: string): string => {
  return html.replace(/\n/g, '<br>')
}

const props = defineProps<{
  tag?: 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  value?: string
  placeholder?: string
  editable?: boolean
  truncate?: boolean
}>()

const tag = computed(() => props.tag || 'div')

const editableElem = ref<HTMLElement | null>(null)
const emit = defineEmits(['input', 'editable_field:save'])
const isEditing = ref(false)
const originalValue = ref(props.value || '')

// This function is called when the user attempts to enter edit mode on the field.
// If not already being edited, it sets the editing state to true,
// stores the current value as the original value, and then focuses the editable div.
// After focusing, it moves the cursor to the end of the content inside the editable div.
const enterEdit = (): void => {
  if (!isEditing.value && props.editable) {
    isEditing.value = true
    originalValue.value = props.value || ''
    nextTick(() => {
      if (editableElem.value) {
        editableElem.value.focus()
        // Create a range that selects all the contents of the editable div,
        // then collapse it to the end so the cursor appears at the end.
        const range = document.createRange()
        range.selectNodeContents(editableElem.value)
        range.collapse(false)
        const sel = window.getSelection()
        if (sel) {
          sel.removeAllRanges()
          sel.addRange(range)
        }
      }
    })
  }
}

// This function is called when the user cancels the edit.
// It restores the original value to the field and sets the editing state to false.
const cancelEdit = (): void => {
  if (isEditing.value && editableElem.value) {
    editableElem.value.innerHTML = originalValue.value || ''
  }
  isEditing.value = false
}

// This function is called when the user saves the edit.
// It checks if the new value is different from the original value and emits the new value.
// Then it sets the editing state to false.
const saveField = (): void => {
  if (!isEditing.value) return
  let newValue = ''

  // Get the new value from the editable div.
  if (editableElem.value) {
    newValue = htmlToPlainText(editableElem.value.innerHTML)
  }

  // If the new value is different from the original value, emit the new value.
  if (newValue !== originalValue.value) {
    emit('input', newValue)

    //This event is to keep the component compatible with the old editable_field:save event
    emit('editable_field:save', newValue)
  }

  // Set the editing state to false.
  isEditing.value = false
}
</script>
<style lang="sass" scoped>
.ue-inline-editable
  transition: box-shadow 0.1s ease-in-out

  &.border-on-hover:hover
    box-shadow: 0 0 0 0.1rem var(--gray-300)
    cursor: text

  &.show-placeholder:empty::before
    content: attr(data-placeholder)
    color: var(--gray-500)
    pointer-events: none

  &[contenteditable='true']:focus
    outline: none
    box-shadow: 0 0 0 0.1rem var(--uePurpleShadow)
</style>
